import { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Paper,
  useTheme,
  alpha,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
} from '@mui/material'
import SortIcon from '@mui/icons-material/Sort'
import StorageIcon from '@mui/icons-material/Storage'
import { useTranslation } from 'react-i18next'
import { backendClient } from '../../../api/backend'

const COUNTRY_FLAGS = {
  MA: '\u{1F1F2}\u{1F1E6}',
  AE: '\u{1F1E6}\u{1F1EA}',
  SA: '\u{1F1F8}\u{1F1E6}',
  BH: '\u{1F1E7}\u{1F1ED}',
  FR: '\u{1F1EB}\u{1F1F7}',
  TN: '\u{1F1F9}\u{1F1F3}',
  DZ: '\u{1F1E9}\u{1F1FF}',
  EG: '\u{1F1EA}\u{1F1EC}',
}

const StatusBadge = ({ status }) => {
  const theme = useTheme()
  const isLive = status === 'live'

  const config = isLive
    ? {
        label: 'Live',
        bg: alpha(theme.palette.success.main, 0.1),
        color: theme.palette.success.main,
        dot: theme.palette.success.main,
      }
    : {
        label: 'Coming Soon',
        bg: alpha(theme.palette.text.secondary, 0.08),
        color: theme.palette.text.secondary,
        dot: theme.palette.text.secondary,
      }

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.6,
        px: 1,
        py: 0.3,
        borderRadius: '20px',
        background: config.bg,
      }}
    >
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: config.color,
          ...(isLive && {
            boxShadow: `0 0 0 2px ${alpha(config.dot, 0.25)}`,
          }),
        }}
      />
      <Typography
        sx={{
          fontSize: '0.72rem',
          fontWeight: 600,
          color: config.color,
          lineHeight: 1,
        }}
      >
        {config.label}
      </Typography>
    </Box>
  )
}

const formatNum = (n) => {
  if (n == null) return '\u2014'
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}m`
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`
  return n.toString()
}

const getJurisdictionDisplayName = (jur, lang) => {
  // Use localized name from JSONB
  if (jur.jurisdiction_name) {
    return jur.jurisdiction_name[lang] || jur.jurisdiction_name.en || jur.jurisdiction_name.fr || jur.jurisdiction_code
  }
  return jur.jurisdiction_code
}

const DataSourcesList = () => {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const theme = useTheme()
  const [sortField, setSortField] = useState('documents')
  const [sortDir, setSortDir] = useState('desc')
  const [loading, setLoading] = useState(true)
  const [sources, setSources] = useState([])
  const [totals, setTotals] = useState({ legislation: 0, case_law: 0 })

  useEffect(() => {
    async function fetchStats() {
      setLoading(true)
      try {
        const data = await backendClient.getDataSourcesStats()
        const items = (data.jurisdictions || []).map((jur) => ({
          id: jur.jurisdiction_code,
          flag: COUNTRY_FLAGS[jur.country_code] || '',
          name: getJurisdictionDisplayName(jur, lang),
          code: jur.jurisdiction_code?.replace(/_/g, '-'),
          documents: jur.legislation_count || 0,
          jurisprudence: jur.case_law_count || 0,
          status: (jur.legislation_count > 0 || jur.case_law_count > 0) ? 'live' : 'coming_soon',
        }))
        setSources(items)
        setTotals(data.totals || { legislation: 0, case_law: 0 })
      } catch (err) {
        console.error('Error fetching data sources stats:', err)
        setSources([])
      } finally {
        setLoading(false)
      }
    }
    fetchStats()
  }, [lang])

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDir('desc')
    }
  }

  const sorted = [...sources].sort((a, b) => {
    if (!sortField) return 0
    const valA = sortField === 'documents' ? a.documents : a.jurisprudence
    const valB = sortField === 'documents' ? b.documents : b.jurisprudence
    return sortDir === 'desc' ? valB - valA : valA - valB
  })

  const goldColor = theme.palette.primary.main

  const headerCellSx = {
    py: 1.25,
    px: { xs: 1.5, sm: 2 },
    fontSize: '0.72rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: theme.palette.text.secondary,
    borderBottom: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    cursor: 'default',
  }

  const bodyCellSx = {
    py: 1.5,
    px: { xs: 1.5, sm: 2 },
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
    fontSize: '0.875rem',
  }

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '12px',
        border: `1px solid ${theme.palette.divider}`,
        background: theme.palette.background.paper,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: { xs: 2, sm: 2.5 },
          py: { xs: 1.75, sm: 2 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${theme.palette.divider}`,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '8px',
              background: alpha(goldColor, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: goldColor,
            }}
          >
            <StorageIcon sx={{ fontSize: 16 }} />
          </Box>
          <Box>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, fontSize: '0.95rem', lineHeight: 1.2 }}
            >
              {t('data_sources') || 'Data Sources'}
            </Typography>
            {!loading && (
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                {t('total_legal_documents') || 'Total legal documents'}: {formatNum(totals.legislation)} ·
                {t('total_jurisprudence') || 'Jurisprudence'}: {formatNum(totals.case_law)}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* Scrollable Table */}
      <TableContainer
        sx={{
          maxHeight: 380,
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: alpha(theme.palette.text.secondary, 0.2),
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: alpha(theme.palette.text.secondary, 0.4),
          },
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx}>{t('jurisdiction')}</TableCell>
              <TableCell
                sx={{ ...headerCellSx, cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('documents')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {t('legal_documents') || 'Legal Docs'}
                  {sortField === 'documents' && (
                    <SortIcon
                      sx={{
                        fontSize: 13,
                        color: goldColor,
                        transform: sortDir === 'asc' ? 'scaleY(-1)' : 'none',
                      }}
                    />
                  )}
                </Box>
              </TableCell>
              <TableCell
                sx={{ ...headerCellSx, cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('jurisprudence')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {t('jurisprudence') || 'Jurisprudence'}
                  {sortField === 'jurisprudence' && (
                    <SortIcon
                      sx={{
                        fontSize: 13,
                        color: goldColor,
                        transform: sortDir === 'asc' ? 'scaleY(-1)' : 'none',
                      }}
                    />
                  )}
                </Box>
              </TableCell>
              <TableCell sx={headerCellSx}>{t('status.label')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell sx={bodyCellSx}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Skeleton variant="circular" width={34} height={34} />
                      <Box>
                        <Skeleton variant="text" width={80} height={16} />
                        <Skeleton variant="text" width={50} height={12} />
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={bodyCellSx}><Skeleton variant="text" width={40} /></TableCell>
                  <TableCell sx={bodyCellSx}><Skeleton variant="text" width={40} /></TableCell>
                  <TableCell sx={bodyCellSx}><Skeleton variant="rectangular" width={60} height={20} sx={{ borderRadius: '10px' }} /></TableCell>
                </TableRow>
              ))
            ) : sorted.length > 0 ? (
              sorted.map((source) => (
                <TableRow
                  key={source.id}
                  sx={{
                    '&:last-child td': { borderBottom: 'none' },
                    '&:hover': {
                      background: alpha(theme.palette.action.hover, 0.5),
                    },
                    transition: 'background 0.15s ease',
                  }}
                >
                  <TableCell sx={bodyCellSx}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: '50%',
                          background: alpha(goldColor, 0.06),
                          border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.1rem',
                          flexShrink: 0,
                        }}
                      >
                        {source.flag}
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                            lineHeight: 1.2,
                          }}
                        >
                          {source.name}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.7rem',
                            color: theme.palette.text.disabled,
                            fontFamily: 'monospace',
                            letterSpacing: '0.05em',
                          }}
                        >
                          {source.code}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    {source.documents > 0 ? (
                      <Typography
                        sx={{
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          color: theme.palette.text.primary,
                        }}
                      >
                        {formatNum(source.documents)}
                      </Typography>
                    ) : (
                      <Typography
                        sx={{
                          fontSize: '0.875rem',
                          fontWeight: 400,
                          color: theme.palette.text.disabled,
                        }}
                      >
                        {'\u2014'}
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    {source.jurisprudence > 0 ? (
                      <Typography
                        sx={{
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          color: theme.palette.text.primary,
                        }}
                      >
                        {formatNum(source.jurisprudence)}
                      </Typography>
                    ) : (
                      <Typography
                        sx={{
                          fontSize: '0.875rem',
                          fontWeight: 400,
                          color: theme.palette.text.disabled,
                        }}
                      >
                        {'\u2014'}
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    <StatusBadge status={source.status} />
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} sx={{ ...bodyCellSx, textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>
                    {t('no_data_available') || 'No data available'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  )
}

export default DataSourcesList
